import {
  createContext,
  useState,
  useCallback,
  useMemo,
  useContext,
  useRef,
  ReactNode,
} from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';
import {
  XIcon,
  CircleCheckIcon,
  SquareXIcon,
  InfoIcon,
  AlertTriangleIcon,
} from 'lucide-react';

/** Default auto-dismiss for toasts when no per-toast duration is set. */
export const DEFAULT_TOAST_DURATION_MS = 8000;

interface Toast {
  message: string | ReactNode;
  type: 'success' | 'error' | 'warning' | 'info';
  /**
   * Override the auto-dismiss timer in ms.  Defaults to the provider's
   * 8000ms.  Pass `Infinity` to keep the toast visible until manually
   * dismissed (or programmatically removed via the returned handle).
   */
  duration?: number;
  /**
   * Whether the user can dismiss the toast via the X button.
   * Defaults to `true`.  Set to `false` for toasts that should only
   * disappear when a programmatic condition is met (e.g. Reconnecting,
   * Checking Status).
   */
  dismissible?: boolean;
}

export interface ToastOptions {
  duration?: number;
  /** @see Toast.dismissible */
  dismissible?: boolean;
}

export type ToastDismiss = () => void;

type ToastDispatch = (
  message: string | ReactNode,
  options?: ToastOptions,
) => ToastDismiss;

const noopDismiss: ToastDismiss = () => {};

const NotificationContext = createContext<{
  success: ToastDispatch;
  error: ToastDispatch;
  warning: ToastDispatch;
  info: ToastDispatch;
}>({
  success: () => noopDismiss,
  error: () => noopDismiss,
  warning: () => noopDismiss,
  info: () => noopDismiss,
});

const getIcon = (type: string) => {
  switch (type) {
    case 'success':
      return <CircleCheckIcon className='text-green-700' />;
    case 'error':
      return <SquareXIcon className='text-red-500' />;
    case 'info':
      return <InfoIcon className='text-indigo-400' />;
    case 'warning':
      return <AlertTriangleIcon className='text-yellow-500' />;
    default:
      return null;
  }
};

const Notifications = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState(new Map());
  const counterRef = useRef(0);

  const handleRemoveToast = useCallback((key: string) => {
    setNotifications((prev) => {
      if (!prev.has(key)) return prev;
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
  }, []);

  const handleAddToast = useCallback(
    (toast: Toast): ToastDismiss => {
      const key = `${Date.now()}-${counterRef.current}`;
      counterRef.current += 1;

      setNotifications((prev) => {
        const newMap = new Map(prev);
        newMap.set(key, { ...toast });
        return newMap;
      });

      return () => handleRemoveToast(key);
    },
    [handleRemoveToast],
  );

  const handleDispatchSuccess = useCallback<ToastDispatch>(
    (message, options) =>
      handleAddToast({
        message,
        type: 'success',
        duration: options?.duration,
        dismissible: options?.dismissible,
      }),
    [handleAddToast],
  );

  const handleDispatchError = useCallback<ToastDispatch>(
    (message, options) =>
      handleAddToast({
        message,
        type: 'error',
        duration: options?.duration,
        dismissible: options?.dismissible,
      }),
    [handleAddToast],
  );

  const handleDispatchWarning = useCallback<ToastDispatch>(
    (message, options) =>
      handleAddToast({
        message,
        type: 'warning',
        duration: options?.duration,
        dismissible: options?.dismissible,
      }),
    [handleAddToast],
  );

  const handleDispatchInfo = useCallback<ToastDispatch>(
    (message, options) =>
      handleAddToast({
        message,
        type: 'info',
        duration: options?.duration,
        dismissible: options?.dismissible,
      }),
    [handleAddToast],
  );

  return (
    <NotificationContext.Provider
      value={useMemo(
        () => ({
          success: handleDispatchSuccess,
          error: handleDispatchError,
          warning: handleDispatchWarning,
          info: handleDispatchInfo,
        }),
        [
          handleDispatchSuccess,
          handleDispatchError,
          handleDispatchWarning,
          handleDispatchInfo,
        ],
      )}
    >
      <ToastPrimitive.Provider duration={DEFAULT_TOAST_DURATION_MS}>
        {children}
        <AnimatePresence>
          {Array.from(notifications).map(([key, notification]) => {
            const isDismissible = notification.dismissible !== false;
            const toastDuration =
              notification.duration ?? DEFAULT_TOAST_DURATION_MS;

            return (
              <ToastPrimitive.Root
                defaultOpen={true}
                duration={toastDuration}
                className={classNames(
                  'flex justify-between items-center py-3.5 pl-4.5 pr-6 w-[300px] rounded-md border bg-background text-foreground shadow-lg',
                  'outline-none focus-visible:shadow-[0_0_0_3px_rgba(2,0,8,0.878)]',
                )}
                onOpenChange={(open) => {
                  // Non-dismissible toasts are removed only via the returned
                  // dismiss handle (programmatic). Ignore Radix close events
                  // from swipe / escape so hooks keep their dismiss refs in sync.
                  if (!open && isDismissible) {
                    handleRemoveToast(key);
                  }
                }}
                onSwipeEnd={(event) => {
                  if (!isDismissible) {
                    event.preventDefault();
                  }
                }}
                onEscapeKeyDown={(event) => {
                  if (!isDismissible) {
                    event.preventDefault();
                  }
                }}
                key={key}
                type={notification.type}
                asChild
                forceMount
              >
                <motion.li
                  className='relative'
                  initial={{
                    y: -100,
                    scale: 0.6,
                    opacity: 0,
                  }}
                  animate={{
                    y: 0,
                    scale: 1,
                    opacity: 1,
                    transition: { duration: 0.3 },
                  }}
                  exit={{
                    scale: 0.9,
                    opacity: 0,
                    transition: { duration: 0.15 },
                  }}
                  layout
                >
                  <div
                    style={{ display: 'flex', gap: 14, alignItems: 'center' }}
                  >
                    <div
                      aria-hidden
                      className={classNames(
                        'flex items-center justify-center w-[24px] h-[24px]',
                      )}
                    >
                      {getIcon(notification.type)}
                    </div>

                    <ToastPrimitive.Description className='text-sm pr-[14px]'>
                      {notification.message}
                    </ToastPrimitive.Description>
                  </div>
                  {isDismissible && (
                    <ToastPrimitive.Close
                      aria-label='Dismiss'
                      className='absolute top-2 right-2 inline-flex size-6 items-center justify-center rounded cursor-pointer text-zinc-500 hover:text-zinc-800 hover:bg-black/5 dark:hover:bg-white/10 transition-colors focus:outline-none z-10'
                    >
                      <XIcon className='size-4' />
                    </ToastPrimitive.Close>
                  )}
                </motion.li>
              </ToastPrimitive.Root>
            );
          })}
        </AnimatePresence>

        <ToastPrimitive.Viewport className='fixed z-60 p-10 flex gap-4 outline-none top-0 flex-col-reverse left-1/2 -translate-x-1/2' />
      </ToastPrimitive.Provider>
    </NotificationContext.Provider>
  );
};

function useNotification() {
  const context = useContext(NotificationContext);
  if (context) return context;
  throw new Error('useNotification must be used within Notifications');
}

export { Notifications, useNotification };
